import React, { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { between, generateId, pascalCase } from './utility/functions';
import Button, { BorderLessButton } from './forms/Button';
import Icon from './forms/Icon';
import Heading from './forms/Heading';
import LoadingEllipsis from './forms/LoadingEllipsis';
import Card from './forms/Card';
import Row from './layout/Row';
import Col from './layout/Col';
import Seasonal from './Seasonal';

const fallDown = keyframes`
    0% { opacity: 0; transform: translateY(-101%); }
    70% { transform: translateY(3%); }
    100% {  opacity: 1; transform: translateY(0%); }
`;
const riseUp = keyframes`
    0% { opacity: 1; transform: translateY(0%); }
    30% { transform: translateY(3%); }
    100% { opacity: 0; transform: translateY(-101%); }
`;
const slideIn = keyframes`
    0% { opacity: 0; transform: translateX(101%); }
    70% { transform: translateX(-3%); }
    100% {  opacity: 1; transform: translateX(0%); }
`;

const slideOut = keyframes`
    0% { opacity: 1; transform: translateX(0%); }
    30% { transform: translateX(3%); }
    100% { opacity: 0; transform: translateX(-101%); }
`;

const Frame = styled.div`
    width: 100svw;
    height: 100svh;
    z-index: 9000;

    position: fixed;
    display: flex;
    justify-content: center;
    align-items: center;
    
    animation: fadeIn;
    animation-duration: 0.3s;
    animation-fill-mode: forwards;

    transition: all 0.3s;

    background: #6c6c6c${({ showBackdrop }) => showBackdrop ? '87' : '00'};
    backdrop-filter: ${({ showBackdrop }) => showBackdrop ? 'blur(10px)' : 'unset'};
    pointer-events: ${({ showBackdrop }) => !showBackdrop ? 'none' : 'auto'};
`;

const Body = styled(Card)`
    padding: 0;
    background: ${({ theme }) => theme.background};
    border-radius: 0.5rem;
    position: relative;

    display: flex;
    align-items: center;
    justify-content: flex-start;

    overflow: hidden;

    box-shadow: 5px 5px 10px 0 #00000082;
    pointer-events: all;
    user-select: none;

    animation: ${({ showing, followUp }) =>
        showing ? followUp ? slideIn : fallDown : followUp ? slideOut : riseUp
    };
    animation-duration: 0.3s;
    animation-fill-mode: forwards;
`;
const Margin = styled.div`
    height: 100%;
    width: 10px;
    position: fixed;
    background: ${({ theme, type }) => theme[type] || theme.alert};
`
const Wrapper = styled.div`
    padding: ${({ mobile }) => mobile ? '0 1em' : '0 2.5rem'};
    max-width: ${({ mobile }) => mobile ? '97svw' : '50svw'};
    min-width: ${({ mobile }) => mobile ? '90svw' : '20svw'};
`
const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`
const Title = styled(Heading)`
    padding: 1.5rem 0;
    font-size: 1.5em;
    font-weight: bold;
    line-height: 1.2;
    display: flex;
    align-items: center;
`
const I = styled(Icon)`
    margin: 0 0.5em 0 0.1em;
    color: ${({ theme, type }) => theme[type] || theme.alert};
    ${({ type }) => type === 'promise' ? 'animation: 2s rotate360 infinite linear;' : ''}
`
const Text = styled.div`
    overflow: overlay;
    padding: ${({ collapse }) => collapse ? 0 : 0.1}em 0;
    max-height: ${({ collapse }) => collapse ? 0 : 65}vh;
`
const Footer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1.5em;
    padding: 1rem 0;
`

const DragRow = styled(Row)`
    background: ${({ theme }) => theme.body};
    overflow: hidden;
    border-radius: 0.2em;
    width: 100%;
`

const DragButton = styled.div`
    width: fit-content;
    display: flex;
    align-items: center;
    background: ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.secondary};
    cursor: ${({ dragging }) => dragging ? 'grabbing' : 'grab'};
    padding: 0 1em;
    height: 2.5em;
    border-radius: 0.2em;
    box-shadow: ${({ theme }) => theme.shadow} -2px 0px 3px 0px;
    ${({ dragging }) => !dragging ? `transition: left 0.32s ease-in` : ''};
`

const MODAL_TYPES = {
    ALERT: 'alert',
    SUCCESS: 'success',
    WARNING: 'warning',
    ERROR: 'error',
    PROMISE: 'promise'
}

const ModalFactory = ({ ...props }) => {
    const generateConfig = () => ({
        id: generateId(),
        showing: true,
        title: '',
        type: MODAL_TYPES.ALERT,
        body: '',
        buttons: [],
        icon: '',
        hideClose: false,
        passthrough: false,
        closeAfter: false,
        slideToClose: false,
        slideToAccept: false,
    })

    const [modals, setModals] = useState([]);
    const [modalsToClose, setModalsToClose] = useState([]);

    useEffect(() => {
        if (modalsToClose.length) {
            setModalsToClose(modals => {
                closeModal(modals.pop());
                return [...modals];
            })
        }
    }, [modalsToClose])

    const addNewModal = (newModal) => {
        newModal = { ...generateConfig(), ...newModal };
        setModals(modals => [...modals, newModal])
        return newModal.id
    }

    const closeModal = (modalId) => {
        setModals((modals) => {
            const modalToClose = modals.find(modal => modal.id === modalId);
            if (!modalToClose) return modals;

            if (modalToClose.finally) {
                modalToClose.finally();
            }

            setTimeout(() => {
                setModals((currentModals) => currentModals.filter(modal => modal.id !== modalId));
            }, 300);

            return modals.map(modal => modal.id === modalId ? close(modal) : modal);
        });
    }

    const update = (newModal) => {
        return new Promise((res) => {
            setModals((prevModals) => {
                const index = prevModals.findIndex(modal => modal.id === newModal.id);
                if (index === -1) return prevModals;

                setTimeout(() => {
                    setModals((current) => current.map(modal => modal.id === newModal.id ? followUp(newModal) : modal));
                    res();
                }, 300);

                return prevModals.map(modal => modal.id === newModal.id ? followClose(modal) : modal);
            });
        })
    };

    const followUp = (modalConfig) => ({ ...modalConfig, followUp: true, showing: true });
    const followClose = (modalConfig) => ({ ...modalConfig, followUp: true, showing: false });
    const close = (modalConfig) => ({ ...modalConfig, followUp: false, showing: false });

    const handleError = (ex) => {
        if (ex.name === 'AbortError') return
        console.log(ex);

        addNewModal({
            title: ex.title || 'Error',
            type: MODAL_TYPES.ERROR,
            body: (ex.errorMessage || ex.error?.errorMessage) ?? <div><p>Oops, something went wrong.</p></div>
        })
    }

    const setToClose = (...id) => setModalsToClose(e => [...e, ...id]);
    const closeAllModals = () => setModalsToClose([...modals.map(e => e.id)])

    ModalFactory.show = addNewModal;
    ModalFactory.update = update;
    ModalFactory.close = setToClose;
    ModalFactory.catch = handleError;
    ModalFactory.closeAll = closeAllModals;

    ModalFactory.promise = function (modal) {
        modal.type = MODAL_TYPES.PROMISE;
        return new Promise((resolve) => {
            const modalId = addNewModal(modal);
            if (modal.callback && typeof modal.callback === 'function') {
                setTimeout(() => {
                    modal.callback()
                        .then((res) => resolve(res, modalId))
                        .catch(ex => {
                            handleError(ex);
                            setToClose(modalId);
                        });
                }, 400);
            }
        });
    };

    ModalFactory.confirmation = function (modal) {
        modal.hideClose = true;
        modal.buttons = modal.buttons || [];
        let modalId = null;
        return new Promise((resolve, reject) => {
            modal.buttons.push(
                modal.slideToAccept
                    ? <SlideToClose close={() => resolve(true)} />
                    : <Button onClick={() => resolve(true)} label={modal.yesLabel || 'Yes'} autoFocus />,
                <Button onClick={() => resolve(false)} label={'No'} />
            );
            modalId = addNewModal(modal);
        })
            .finally(() => {
                if (modal.closeAfter) setToClose(modalId);
            });
    };

    ModalFactory.confirmAndProcess = function (config) {
        const id = generateId();
        const { confirmation, promise, confirmed } = config
        ModalFactory.confirmation({
            id,
            ...confirmation,
        })
            .then((res) => {
                if (!res) {
                    setToClose(id)
                    return
                }
                if (promise.callback && typeof promise.callback === 'function') {
                    update({
                        id,
                        ...promise,
                        type: MODAL_TYPES.PROMISE
                    })
                    setTimeout(() => {
                        promise.callback()
                            .then((res) => {
                                if (config.finally && typeof config.finally === 'function') {
                                    config.finally(res, id);
                                }
                                if (confirmed) {
                                    update({
                                        id,
                                        ...confirmed,
                                        type: MODAL_TYPES.SUCCESS
                                    })
                                } else {
                                    setToClose(id);
                                }
                            })
                            .catch(ex => {
                                handleError(ex);
                                setToClose(id);
                            });
                    }, 400);
                }
            })
    }

    return modals.sort((a, b) => a.passthrough - b.passthrough).map(modal => (<Modal key={modal.id} closeModal={setToClose} {...modal} />))
}

export default ModalFactory;

const Modal = ({ closeModal, ...props }) => {
    const { followUp, showing, id, type, title, body, icon, data, buttons = [], hideClose, passthrough, slideToClose, slideToAccept } = props;

    const modalRef = useRef(null);
    const [collapse, setCollapse] = useState(false);
    const [promiseTimeout, setPromiseTimeout] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [pos, setPos] = useState({ x: 0, y: 0 });

    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;

    const getMinMax = () => {
        const rect = modalRef.current.getBoundingClientRect();
        return {
            minX: -centerX + rect.width / 2,
            minY: -centerY + rect.height / 2,
            maxX: centerX - (rect.width / 2),
            maxY: centerY + (rect.height / 2) - 50,
            width: rect.width,
            height: rect.height
        }
    }

    useEffect(() => {
        const timeoutId = setTimeout(() => { setPromiseTimeout(type === MODAL_TYPES.PROMISE) }, 5000);
        return () => clearTimeout(timeoutId);
    }, [body, type]);


    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!dragging || !modalRef) return;
            const { minX, minY, maxX, maxY, height } = getMinMax();
            const pointerX = (e.clientX - centerX);
            const pointerY = (e.clientY - centerY) + (height / 2) - 20;
            setPos({
                x: between(pointerX, minX, maxX),
                y: between(pointerY, minY, maxY)
            });
        };

        const handleMouseUp = () => { setDragging(false) }

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        }
    }, [dragging])

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const { minX, minY, maxX, maxY } = getMinMax();
            setPos({
                x: between(pos.x, minX, maxX),
                y: between(pos.y, minY, maxY)
            });
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [collapse])

    const iconType = (type) => {
        switch (type) {
            case MODAL_TYPES.SUCCESS:
                return 'checkCircle';
            case MODAL_TYPES.WARNING:
                return 'exclamationCircle';
            case MODAL_TYPES.ERROR:
                return 'exclamationTriangle';
            case MODAL_TYPES.PROMISE:
                return 'hourglass';
            default:
                return 'infoCircle';
        }
    }

    const showCloseButton = (slideToClose || slideToAccept) ? false : (type !== MODAL_TYPES.PROMISE && !hideClose) || promiseTimeout;

    const mobile = window.innerWidth < 1200;

    const showBackdrop = (!passthrough && !collapse) && showing;

    return (
        <Frame
            showBackdrop={showBackdrop}
            key={id}
        >
            {showBackdrop && <Seasonal />}
            <Body
                showing={showing}
                followUp={followUp}
                style={{ top: pos.y, left: pos.x, transition: dragging ? 'unset' : 'all 0.15s' }}
                dragging={dragging}
                mobile={mobile}
            >
                <div ref={modalRef}>
                    <Margin type={type} />
                    <Wrapper
                        mobile={mobile}
                    >
                        <Row justify='space-between'>
                            <Header
                                style={{ cursor: dragging ? 'grabbing' : 'grab' }}
                                onMouseDown={(e) => {
                                    setDragging(true);
                                }}
                            >
                                <Title style={{ cursor: dragging ? 'grabbing' : 'grab' }}>
                                    <I type={type} icon={icon || iconType(type)} />
                                    <span autoFocus>{typeof title === 'string' ? pascalCase(title) : title}{type === MODAL_TYPES.PROMISE && <LoadingEllipsis />}</span>
                                </Title>
                            </Header>
                            {type !== MODAL_TYPES.PROMISE && (
                                <BorderLessButton
                                    icon='chevronDown'
                                    onClick={() => setCollapse(!collapse)}
                                    style={{ transform: `rotate(${collapse ? 0 : 180}deg)` }}
                                />
                            )}
                        </Row>

                        <div>
                            <Text
                                collapse={collapse}
                            >
                                {typeof body === 'function' ? body(data) : body}
                                {promiseTimeout ? <div>Taking longer than usual...</div> : null}
                            </Text>
                            <Footer>
                                {buttons.map((e, i) => <Fragment key={`ModalButton_${i}`}>{e}</Fragment>)}
                                {showCloseButton && <Button autoFocus onClick={() => closeModal(id)}>Close</Button>}
                                {slideToClose && <SlideToClose close={() => closeModal(id)} />}
                            </Footer>
                        </div>
                    </Wrapper>
                </div>
            </Body>
        </Frame>
    )
}

const SlideToClose = ({ close, ...props }) => {
    const dragRef = useRef(null);

    const [dragging, setDragging] = useState(false);
    const [pos, setPos] = useState(0);
    const [max, setMax] = useState(0);

    useLayoutEffect(() => {
        const rect = dragRef.current.getBoundingClientRect();
        setMax(Math.round(rect.width));
    }, [])

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!dragging || !dragRef) return;
            const rect = dragRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left - 20;
            setPos(between(x, 0, max));
        };
        const handleMouseUp = () => {
            if (max && pos && pos === max) {
                close();
                return;
            }
            setDragging(false);
            setPos(0);
        }

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        }
    }, [dragging, pos])

    return (
        <DragRow>
            <div ref={dragRef} style={{ width: 'calc(100% - 2.8em)' }}>
                <DragButton
                    style={{ left: pos, position: 'relative' }}
                    onMouseDown={() => setDragging(true)}
                    dragging={dragging}
                >
                    <Icon icon={pos === max ? 'check' : 'arrowRight'} />
                </DragButton>
            </div>
            <sup
                style={{
                    left: '8em',
                    position: 'absolute',
                    opacity: dragging ? 0 : 1,
                    transition: 'opacity 0.3s'
                }}
            >Drag to accept</sup>
        </DragRow>
    )
}
